import { useEffect, useState } from 'react';
import { KEYBOARD_MIN } from './useKeyboardUp';

/** A field the phone raises its keyboard for. */
const isTextField = (el: Element | null): boolean => {
  if (!el) return false;
  if (el.tagName === 'TEXTAREA') return true;
  if ((el as HTMLElement).isContentEditable) return true;
  if (el.tagName !== 'INPUT') return false;
  return ['text', 'search', 'email', 'tel', 'url', 'number', 'password', ''].includes((el as HTMLInputElement).type);
};

/**
 * What a keyboard is up for: 'none'; 'dock' - a field inside a box that sits ON the keyboard (marked
 * `data-sits-on-keyboard`: the Morning chat box); 'field' - any other text field.
 */
export type KeyboardOpen = 'none' | 'field' | 'dock';

/**
 * CHAT-DUET (2026-10-28): a keyboard is up - ANY text field on the page has the focus and the visible area is at least
 * KEYBOARD_MIN shorter than the window (iOS and Android shrink the visual viewport, not `innerHeight`; a desktop never
 * does, so there it stays 'none'). The phone bar hides while one is up; it gives its room back only for 'dock' - for any
 * other field the page under the field must not move (the review + the iPhone: Convo's reply box slid under the keys).
 * (`useKeyboardUp` is the Morning chat box's own, narrower question: is he typing IN THE BOX.)
 */
export function useKeyboardOpen(): KeyboardOpen {
  const [open, setOpen] = useState<KeyboardOpen>('none');
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    // No visual viewport (an old browser, a test without one): never "open".
    if (!vv) return undefined;
    const measure = () => {
      const el = document.activeElement;
      if (!isTextField(el) || window.innerHeight - vv.height < KEYBOARD_MIN) { setOpen('none'); return; }
      setOpen(el?.closest('[data-sits-on-keyboard]') ? 'dock' : 'field');
    };
    vv.addEventListener('resize', measure);
    vv.addEventListener('scroll', measure);
    document.addEventListener('focusin', measure);
    document.addEventListener('focusout', measure);
    measure();
    return () => {
      vv.removeEventListener('resize', measure);
      vv.removeEventListener('scroll', measure);
      document.removeEventListener('focusin', measure);
      document.removeEventListener('focusout', measure);
    };
  }, []);
  return open;
}
